import { z } from "zod";
import type { TRequest } from "./request";

/** How much we do for the client on a request. Same process either way; it
 *  sets the price and tells the admin what was promised. Admin-only: never
 *  shown to the client. */
export const ServiceLevelSchema = z.enum(["full", "basic"]);
export type TServiceLevel = z.infer<typeof ServiceLevelSchema>;

export const serviceLevelLabels: Record<TServiceLevel, string> = {
	full: "שירות מלא",
	basic: "שירות בסיסי",
};

/** Price per service level, set by an admin (settings/pricing). A level with
 *  no price means: any payment settles the request. */
export type TPaymentStatus = NonNullable<TRequest["paymentDetails"]>["status"];

export const paymentStatusLabels: Record<TPaymentStatus, string> = {
	pending: "ממתין לתשלום",
	paid: "שולם",
	refund: "הוחזר",
	partially_paid: "שולם חלקית",
	cancelled: "בוטל",
};

/** What every payment is for — shown to the patient on the Hyp page and receipt. */
export const PAYMENT_TITLE = "עבור חידוש רישיון / הגדלת מינון";

export const ServicePricingSchema = z.object({
	full: z.number().positive().nullish(),
	basic: z.number().positive().nullish(),
});
export type TServicePricing = z.infer<typeof ServicePricingSchema>;

/** A request filed by an admin (or broker) is full service; one a client filed
 *  on their own is basic. Requests from before the field existed follow the
 *  same rule, so they need no migration. */
export function serviceLevelOf(request: Pick<TRequest, "serviceLevel" | "createdBy">): TServiceLevel {
	return request.serviceLevel ?? (request.createdBy === "client" ? "basic" : "full");
}

export function paidTotal(request: Pick<TRequest, "paymentDetails">): number {
	return request.paymentDetails?.payments?.reduce((sum, payment) => sum + payment.amount, 0) ?? 0;
}

/**
 * The price saved on the request — set from its level's price when it was
 * opened, or by an admin since. Never read from the current settings: a later
 * price change must not reprice requests already open (one opened with no
 * price stays "any payment settles it").
 */
export function requestCost(request: Pick<TRequest, "paymentDetails">): number | undefined {
	return request.paymentDetails?.cost || undefined;
}

/** Nothing paid → pending. With no price set, any payment settles it. */
export function paymentStatusFor(total: number, cost: number | undefined): "pending" | "paid" | "partially_paid" {
	if (total <= 0) return "pending";
	if (!cost || total >= cost) return "paid";
	return "partially_paid";
}
